// src/config/env.ts
import { z } from "zod";

// Define the environment schema
const EnvSchema = z.object({
  VITE_SUPABASE_URL: z.string().url(),
  VITE_SUPABASE_ANON_KEY: z.string().min(10),
  VITE_SITE_URL: z.string().url().optional(),
  VITE_AUTO_CONFIRM_USERS: z.string().optional(),
  VITE_DISABLE_EMAIL_CONFIRMATION: z.string().optional(),
  VITE_STRIPE_PUBLISHABLE_KEY: z.string().optional(),
  VITE_SENTRY_DSN: z.string().optional(),
  VITE_MONITORING_ENDPOINT: z.string().optional(),
  VITE_FEATURE_TIME_AUDIT: z.string().optional(),
  VITE_FEATURE_MIT_MODE: z.string().optional(),
  VITE_FEATURE_CONTRIB_REQUIRED: z.string().optional(),
  VITE_FEATURE_ABANDONMENT_SWEEP: z.string().optional(),
  VITE_FEATURE_DECISION_CARDS: z.string().optional(),
  VITE_FEATURE_PPM_INTEGRATION: z.string().optional(),
  VITE_FEATURE_SMARTSHEET_SYNC: z.string().optional(),
  VITE_FEATURE_GOOGLESHEETS_SYNC: z.string().optional(),
  VITE_FEATURE_COMPLIANCE_MODE: z.string().optional(),
  VITE_FEATURE_STAGE_GATES: z.string().optional(),
  VITE_FEATURE_AUDIT_TRAIL: z.string().optional(),
  VITE_FEATURE_IMMUTABLE_LOG: z.string().optional(),
  VITE_FEATURE_ASSISTED_CARE: z.string().optional(),
  VITE_FAMILY_ONLY_MODE: z.string().optional(),
  VITE_GIT_BRANCH: z.string().optional(),
  VITE_GIT_COMMIT: z.string().optional(),
  VITE_BUILD_TIME: z.string().optional(),
});

// Create a lazy-loaded environment object
let _env: z.infer<typeof EnvSchema> | null = null;

function getEnv(): z.infer<typeof EnvSchema> {
  if (_env !== null) {
    return _env;
  }

  try {
    // Only access import.meta.env when this function is called
    const envData = {
      VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL || "",
      VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY || "",
      VITE_SITE_URL: import.meta.env.VITE_SITE_URL || "",
      VITE_AUTO_CONFIRM_USERS: import.meta.env.VITE_AUTO_CONFIRM_USERS || "",
      VITE_DISABLE_EMAIL_CONFIRMATION:
        import.meta.env.VITE_DISABLE_EMAIL_CONFIRMATION || "",
      VITE_STRIPE_PUBLISHABLE_KEY:
        import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "",
      VITE_SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN || "",
      VITE_MONITORING_ENDPOINT: import.meta.env.VITE_MONITORING_ENDPOINT || "",
      VITE_FEATURE_TIME_AUDIT: import.meta.env.VITE_FEATURE_TIME_AUDIT || "",
      VITE_FEATURE_MIT_MODE: import.meta.env.VITE_FEATURE_MIT_MODE || "",
      VITE_FEATURE_CONTRIB_REQUIRED:
        import.meta.env.VITE_FEATURE_CONTRIB_REQUIRED || "",
      VITE_FEATURE_ABANDONMENT_SWEEP:
        import.meta.env.VITE_FEATURE_ABANDONMENT_SWEEP || "",
      VITE_FEATURE_DECISION_CARDS:
        import.meta.env.VITE_FEATURE_DECISION_CARDS || "",
      VITE_FEATURE_PPM_INTEGRATION:
        import.meta.env.VITE_FEATURE_PPM_INTEGRATION || "",
      VITE_FEATURE_SMARTSHEET_SYNC:
        import.meta.env.VITE_FEATURE_SMARTSHEET_SYNC || "",
      VITE_FEATURE_GOOGLESHEETS_SYNC:
        import.meta.env.VITE_FEATURE_GOOGLESHEETS_SYNC || "",
      VITE_FEATURE_COMPLIANCE_MODE:
        import.meta.env.VITE_FEATURE_COMPLIANCE_MODE || "",
      VITE_FEATURE_STAGE_GATES: import.meta.env.VITE_FEATURE_STAGE_GATES || "",
      VITE_FEATURE_AUDIT_TRAIL: import.meta.env.VITE_FEATURE_AUDIT_TRAIL || "",
      VITE_FEATURE_IMMUTABLE_LOG: import.meta.env.VITE_FEATURE_IMMUTABLE_LOG ||
        "",
      VITE_FEATURE_ASSISTED_CARE: import.meta.env.VITE_FEATURE_ASSISTED_CARE ||
        "",
      VITE_FAMILY_ONLY_MODE: import.meta.env.VITE_FAMILY_ONLY_MODE || "",
      VITE_GIT_BRANCH: import.meta.env.VITE_GIT_BRANCH || "",
      VITE_GIT_COMMIT: import.meta.env.VITE_GIT_COMMIT || "",
      VITE_BUILD_TIME: import.meta.env.VITE_BUILD_TIME || "",
    };

    const result = EnvSchema.safeParse(envData);

    if (!result.success) {
      console.warn("Environment validation failed:", result.error.issues);

      // Return fallback values
      _env = {
        VITE_SUPABASE_URL: envData.VITE_SUPABASE_URL ||
          "http://localhost:54321",
        VITE_SUPABASE_ANON_KEY: envData.VITE_SUPABASE_ANON_KEY ||
          "development-key",
        VITE_SITE_URL: envData.VITE_SITE_URL,
        VITE_AUTO_CONFIRM_USERS: envData.VITE_AUTO_CONFIRM_USERS,
        VITE_DISABLE_EMAIL_CONFIRMATION:
          envData.VITE_DISABLE_EMAIL_CONFIRMATION,
        VITE_STRIPE_PUBLISHABLE_KEY: envData.VITE_STRIPE_PUBLISHABLE_KEY,
        VITE_SENTRY_DSN: envData.VITE_SENTRY_DSN,
        VITE_MONITORING_ENDPOINT: envData.VITE_MONITORING_ENDPOINT,
        VITE_FEATURE_TIME_AUDIT: envData.VITE_FEATURE_TIME_AUDIT,
        VITE_FEATURE_MIT_MODE: envData.VITE_FEATURE_MIT_MODE,
        VITE_FEATURE_CONTRIB_REQUIRED: envData.VITE_FEATURE_CONTRIB_REQUIRED,
        VITE_FEATURE_ABANDONMENT_SWEEP: envData.VITE_FEATURE_ABANDONMENT_SWEEP,
        VITE_FEATURE_DECISION_CARDS: envData.VITE_FEATURE_DECISION_CARDS,
        VITE_FEATURE_PPM_INTEGRATION: envData.VITE_FEATURE_PPM_INTEGRATION,
        VITE_FEATURE_SMARTSHEET_SYNC: envData.VITE_FEATURE_SMARTSHEET_SYNC,
        VITE_FEATURE_GOOGLESHEETS_SYNC: envData.VITE_FEATURE_GOOGLESHEETS_SYNC,
        VITE_FEATURE_COMPLIANCE_MODE: envData.VITE_FEATURE_COMPLIANCE_MODE,
        VITE_FEATURE_STAGE_GATES: envData.VITE_FEATURE_STAGE_GATES,
        VITE_FEATURE_AUDIT_TRAIL: envData.VITE_FEATURE_AUDIT_TRAIL,
        VITE_FEATURE_IMMUTABLE_LOG: envData.VITE_FEATURE_IMMUTABLE_LOG,
        VITE_FEATURE_ASSISTED_CARE: envData.VITE_FEATURE_ASSISTED_CARE,
        VITE_FAMILY_ONLY_MODE: envData.VITE_FAMILY_ONLY_MODE,
        VITE_GIT_BRANCH: envData.VITE_GIT_BRANCH,
        VITE_GIT_COMMIT: envData.VITE_GIT_COMMIT,
        VITE_BUILD_TIME: envData.VITE_BUILD_TIME,
      };
    } else {
      _env = result.data;
    }

    return _env;
  } catch (error) {
    console.error("Error accessing environment variables:", error);

    // Return safe fallback values
    _env = {
      VITE_SUPABASE_URL: "http://localhost:54321",
      VITE_SUPABASE_ANON_KEY: "development-key",
      VITE_SITE_URL: "",
      VITE_AUTO_CONFIRM_USERS: "",
      VITE_DISABLE_EMAIL_CONFIRMATION: "",
      VITE_STRIPE_PUBLISHABLE_KEY: "",
      VITE_SENTRY_DSN: "",
      VITE_MONITORING_ENDPOINT: "",
      VITE_FEATURE_TIME_AUDIT: "",
      VITE_FEATURE_MIT_MODE: "",
      VITE_FEATURE_CONTRIB_REQUIRED: "",
      VITE_FEATURE_ABANDONMENT_SWEEP: "",
      VITE_FEATURE_DECISION_CARDS: "",
      VITE_FEATURE_PPM_INTEGRATION: "",
      VITE_FEATURE_SMARTSHEET_SYNC: "",
      VITE_FEATURE_GOOGLESHEETS_SYNC: "",
      VITE_FEATURE_COMPLIANCE_MODE: "",
      VITE_FEATURE_STAGE_GATES: "",
      VITE_FEATURE_AUDIT_TRAIL: "",
      VITE_FEATURE_IMMUTABLE_LOG: "",
      VITE_FEATURE_ASSISTED_CARE: "",
      VITE_FAMILY_ONLY_MODE: "",
      VITE_GIT_BRANCH: "",
      VITE_GIT_COMMIT: "",
      VITE_BUILD_TIME: "",
    };

    return _env;
  }
}

// Export a proxy that only accesses environment variables when properties are accessed
export const env = new Proxy({} as z.infer<typeof EnvSchema>, {
  get(target, prop) {
    if (typeof prop === "string") {
      return getEnv()[prop as keyof z.infer<typeof EnvSchema>];
    }
    return undefined;
  },
});

// Type-safe environment variables
export type Env = z.infer<typeof EnvSchema>;
